import { useState } from "react";
import { LegalLayout, LegalH2, LegalP, LegalUl } from "@/components/LegalLayout";

const Strong = ({ children }) => <strong className="text-white/85">{children}</strong>;

const PrivacyIT = () => (
  <>
    <LegalP>
      La presente informativa descrive come Liqo (“l'App”, “noi”) tratta i dati personali degli utenti, ai sensi
      del Regolamento (UE) 2016/679 (“GDPR”). Liqo è un assistente di finanza personale per freelance che aggrega
      e analizza i tuoi dati finanziari per fornirti insight, previsioni e un assistente conversazionale (“CFO AI”).
    </LegalP>
    <LegalP>
      <Strong>Titolare del trattamento:</Strong> [Ragione Sociale / Nome Titolare] — [indirizzo] —
      email: privacy@liqo.finance — sito: liqo.finance. (Da completare prima della pubblicazione.)
    </LegalP>

    <LegalH2>Dati che trattiamo</LegalH2>
    <LegalUl
      items={[
        "Dati account: email, nome, e (per il login email) password in forma cifrata (hash).",
        "Dati bancari via Open Banking (PSD2): saldi, conti e transazioni, ottenuti tramite il provider autorizzato Enable Banking, solo dopo il tuo consenso esplicito presso la tua banca.",
        "Dati PayPal (se collegato): movimenti e saldo tramite le API ufficiali PayPal.",
        "Dati di portafoglio inseriti da te: posizioni, broker, prezzi manuali.",
        "Profilo finanziario: tipo di lavoro, giorno di accredito, budget, profilo di rischio.",
        "Contenuti della chat con il CFO AI e feedback sulle categorie.",
        "Dati tecnici minimi: log di servizio e diagnostica.",
      ]}
    />

    <LegalH2>Finalità e basi giuridiche</LegalH2>
    <LegalUl
      items={[
        "Erogazione del servizio (esecuzione del contratto): classificazione transazioni, insight, previsioni, stima tasse, CFO AI.",
        "Connessione bancaria (consenso): accesso ai dati di conto tramite PSD2, revocabile in qualsiasi momento.",
        "Sicurezza e prevenzione abusi (legittimo interesse).",
        "Adempimenti di legge, ove applicabili.",
      ]}
    />

    <LegalH2>Open Banking (PSD2)</LegalH2>
    <LegalP>
      Il collegamento ai conti avviene esclusivamente tramite <Strong>Enable Banking</Strong>, provider di servizi
      di informazione sui conti (AISP) autorizzato ai sensi della direttiva PSD2. L'accesso è in <Strong>sola
      lettura</Strong>: Liqo non può disporre pagamenti né trasferire fondi. Liqo <Strong>non vede né memorizza le
      credenziali della tua banca</Strong>: l'autenticazione avviene presso la banca stessa. Il consenso ha durata
      limitata (fino a ~90 giorni) ed è <Strong>revocabile in qualsiasi momento</Strong> disconnettendo la banca
      dall'app o presso il tuo istituto.
    </LegalP>

    <LegalH2>Intelligenza Artificiale</LegalH2>
    <LegalP>
      Per generare le card e le risposte del CFO AI, estratti aggregati dei tuoi dati finanziari (es. saldi,
      importi, categorie) vengono inviati a un fornitore di modelli linguistici tramite un'infrastruttura gestita.
      Non vengono trasmesse credenziali bancarie. Le risposte hanno finalità informativa ed educativa e{" "}
      <Strong>non costituiscono consulenza finanziaria, fiscale o di investimento</Strong>.
    </LegalP>

    <LegalH2>Condivisione dei dati</LegalH2>
    <LegalP>
      Non vendiamo i tuoi dati. Li condividiamo solo con fornitori (“responsabili del trattamento”) necessari al
      funzionamento del servizio: provider Open Banking (Enable Banking), PayPal, fornitori di prezzi di mercato,
      fornitore di modelli AI e hosting. Ciascuno tratta i dati secondo istruzioni e adeguate garanzie.
    </LegalP>

    <LegalH2>Conservazione</LegalH2>
    <LegalP>
      Conserviamo i dati per il tempo necessario all'erogazione del servizio. Puoi disconnettere le banche in
      qualsiasi momento e richiedere la cancellazione dell'account: in tal caso i dati associati vengono eliminati,
      salvo obblighi di legge.
    </LegalP>

    <LegalH2>Cancellazione dell'account e dei dati</LegalH2>
    <LegalP>
      Puoi eliminare in autonomia il tuo account dalla schermata Account dell'app (“Elimina account”). La
      cancellazione comporta l'eliminazione del profilo utente e di tutti i dati finanziari associati (transazioni,
      card, portafoglio, connessioni bancarie) dal nostro database. In alternativa puoi scriverci a privacy@liqo.finance.
    </LegalP>

    <LegalH2>I tuoi diritti (GDPR)</LegalH2>
    <LegalUl
      items={[
        "Accesso, rettifica, cancellazione (“diritto all'oblio”).",
        "Limitazione e opposizione al trattamento.",
        "Portabilità dei dati.",
        "Revoca del consenso (es. disconnessione banca) in qualsiasi momento.",
        "Reclamo all'Autorità Garante per la protezione dei dati personali.",
      ]}
    />
    <LegalP>
      Per esercitare i tuoi diritti o eliminare l'account scrivi a privacy@liqo.finance oppure usa la funzione
      “Elimina account” nelle impostazioni dell'app.
    </LegalP>

    <LegalH2>Sicurezza</LegalH2>
    <LegalP>
      Adottiamo misure tecniche e organizzative adeguate (cifratura in transito, hashing delle password, accesso
      limitato). Nessun sistema è sicuro al 100%: ti invitiamo a custodire le tue credenziali.
    </LegalP>

    <LegalH2>Contatti</LegalH2>
    <LegalP>Per qualsiasi richiesta: privacy@liqo.finance.</LegalP>
  </>
);

const PrivacyEN = () => (
  <>
    <LegalP>
      This notice describes how Liqo (“the App”, “we”) processes users' personal data under Regulation (EU)
      2016/679 (“GDPR”). Liqo is a personal finance assistant for freelancers that aggregates and analyses your
      financial data to provide insights, forecasts and a conversational assistant (“AI CFO”).
    </LegalP>
    <LegalP>
      <Strong>Data Controller:</Strong> [Company / Controller name] — [address] — email: privacy@liqo.finance — website: liqo.finance.
      (To be completed before publication.)
    </LegalP>

    <LegalH2>Data we process</LegalH2>
    <LegalUl
      items={[
        "Account data: email, name and (for email login) password in hashed form.",
        "Bank data via Open Banking (PSD2): balances, accounts and transactions, obtained through the authorised provider Enable Banking, only after your explicit consent at your bank.",
        "PayPal data (if connected): transactions and balance via the official PayPal APIs.",
        "Portfolio data entered by you: holdings, broker, manual prices.",
        "Financial profile: job type, pay day, budget, risk profile.",
        "AI CFO chat content and category feedback.",
        "Minimal technical data: service logs and diagnostics.",
      ]}
    />

    <LegalH2>Purposes and legal bases</LegalH2>
    <LegalUl
      items={[
        "Service delivery (performance of a contract): transaction classification, insights, forecasts, tax estimates, AI CFO.",
        "Bank connection (consent): access to account data via PSD2, revocable at any time.",
        "Security and abuse prevention (legitimate interest).",
        "Legal obligations, where applicable.",
      ]}
    />

    <LegalH2>Open Banking (PSD2)</LegalH2>
    <LegalP>
      Account linking is performed exclusively through <Strong>Enable Banking</Strong>, an Account Information
      Service Provider (AISP) authorised under the PSD2 directive. Access is <Strong>read-only</Strong>: Liqo cannot
      initiate payments or move funds. Liqo <Strong>never sees or stores your bank credentials</Strong>:
      authentication takes place at your bank. Consent has a limited duration (up to ~90 days) and can be{" "}
      <Strong>revoked at any time</Strong> by disconnecting the bank in the app or at your institution.
    </LegalP>

    <LegalH2>Artificial Intelligence</LegalH2>
    <LegalP>
      To generate cards and AI CFO answers, aggregated extracts of your financial data (e.g. balances, amounts,
      categories) are sent to a language-model provider via a managed infrastructure. Bank credentials are never
      transmitted. Answers are for informational and educational purposes and{" "}
      <Strong>do not constitute financial, tax or investment advice</Strong>.
    </LegalP>

    <LegalH2>Data sharing</LegalH2>
    <LegalP>
      We do not sell your data. We share it only with providers (“data processors”) necessary to operate the
      service: Open Banking provider (Enable Banking), PayPal, market-price providers, the AI model provider and
      hosting. Each processes data under instructions and appropriate safeguards.
    </LegalP>

    <LegalH2>Retention</LegalH2>
    <LegalP>
      We keep data for as long as necessary to provide the service. You can disconnect banks at any time and
      request account deletion: in that case the associated data is erased, except where legal obligations apply.
    </LegalP>

    <LegalH2>Account and data deletion</LegalH2>
    <LegalP>
      You can delete your account yourself from the app's Account screen (“Delete account”). Deletion removes your
      user profile and all associated financial data (transactions, cards, portfolio, bank connections) from our
      database. Alternatively, contact us at privacy@liqo.finance.
    </LegalP>

    <LegalH2>Your rights (GDPR)</LegalH2>
    <LegalUl
      items={[
        "Access, rectification, erasure (“right to be forgotten”).",
        "Restriction of and objection to processing.",
        "Data portability.",
        "Withdrawal of consent (e.g. bank disconnection) at any time.",
        "Lodge a complaint with the competent Data Protection Authority.",
      ]}
    />
    <LegalP>
      To exercise your rights or delete your account, contact privacy@liqo.finance or use the “Delete account” function
      in the app settings.
    </LegalP>

    <LegalH2>Security</LegalH2>
    <LegalP>
      We apply appropriate technical and organisational measures (encryption in transit, password hashing,
      restricted access). No system is 100% secure: please keep your credentials safe.
    </LegalP>

    <LegalH2>Contact</LegalH2>
    <LegalP>For any request: privacy@liqo.finance.</LegalP>
  </>
);

export default function Privacy() {
  const [lang, setLang] = useState("it");
  const meta =
    lang === "it"
      ? { title: "Informativa sulla Privacy", updated: "Ultimo aggiornamento: Giugno 2026" }
      : { title: "Privacy Policy", updated: "Last updated: June 2026" };
  return (
    <LegalLayout title={meta.title} updated={meta.updated} testid="privacy-page" lang={lang} setLang={setLang}>
      {lang === "it" ? <PrivacyIT /> : <PrivacyEN />}
    </LegalLayout>
  );
}
